<template>
  <div class="welcome-page">
    <template v-if="shouldDisplaySiteSelector">
      <div class="onboarding-shell">
        <div class="onboarding-card">
          <div class="onboarding-header">
            <div class="se-logo" />
            <div class="dij-logo onboarding-dij-logo" aria-hidden="true">
              <div class="abbreviation">DIJ</div>
              <div class="name">
                <div class="name-top">{{ $t('login_page.digital') }}</div>
                <div class="name-bottom">
                  {{ $t('login_page.inspection_jacket') }}
                </div>
              </div>
            </div>
            <h1 class="welcome-message">Onboarding Flow</h1>
            <p class="onboarding-note">
              Please select the relevant location(s) and the role for your
              access request.
            </p>
            <p class="step-indicator">Step {{ onboardingStep }} of 2</p>
          </div>

          <div class="stepper">
            <div
              class="step-item"
              :class="{
                active: onboardingStep === 1,
                done: onboardingStep > 1,
              }"
            >
              <span class="step-number">1</span>
              <span class="step-label">Location</span>
            </div>
            <div class="step-divider"></div>
            <div class="step-item" :class="{ active: onboardingStep === 2 }">
              <span class="step-number">2</span>
              <span class="step-label">Role</span>
            </div>
          </div>

          <div class="onboarding-body">
            <template v-if="onboardingStep === 1">
              <p class="step-title">Select your location(s)</p>
              <p class="step-description">
                Choose one primary location and any secondary locations.
              </p>
              <select-sites
                @selectedSites="handleSelectedSites($event)"
              ></select-sites>
            </template>

            <template v-else>
              <p class="step-title">Select the role you are requesting</p>
              <p class="step-description">
                This role will be sent for admin approval with your location
                selection.
              </p>
              <div class="selected-locations-summary">
                <p class="summary-title">Selected locations</p>
                <p class="summary-item">
                  <strong>Primary:</strong>
                  {{ selectedPrimaryLocationName || 'Not selected' }}
                </p>
                <p
                  class="summary-item"
                  v-if="selectedSecondaryLocationNames.length > 0"
                >
                  <strong>Secondary:</strong>
                  {{ selectedSecondaryLocationNames.join(', ') }}
                </p>
              </div>
              <div class="role-picker">
                <b-field label="Role">
                  <b-select
                    v-model="selectedRole"
                    placeholder="Select a role"
                    expanded
                  >
                    <option
                      v-for="role in requestableRoles"
                      :key="role._id"
                      :value="role.rolename"
                    >
                      {{ role.rolename }}
                    </option>
                  </b-select>
                </b-field>
              </div>
            </template>
          </div>

          <div class="actions">
            <b-button v-if="isSurrogating" @click="stopSurrogating">{{
              $t('stop_surrogating')
            }}</b-button>
            <b-button v-if="onboardingStep === 2" @click="goToPreviousStep"
              >Back</b-button
            >
            <b-button
              v-if="onboardingStep === 1"
              type="is-primary"
              :disabled="!hasValidPrimarySite"
              @click="goToNextStep"
              >Next</b-button
            >
            <b-button
              v-else
              type="is-primary"
              :disabled="!canSubmitOnboardingRequest"
              @click="submitOnboardingRequest"
              >Submit request</b-button
            >
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="pending-shell">
        <div class="pending-card">
          <div class="se-logo" />
          <div class="content">
            <div class="dij-logo">
              <div class="abbreviation">DIJ</div>
              <div class="name">
                <div class="name-top">{{ $t('login_page.digital') }}</div>
                <div class="name-bottom">
                  {{ $t('login_page.inspection_jacket') }}
                </div>
              </div>
            </div>
            <div class="pending-approval-message">
              <div class="part1">
                {{ $t('new_user_welcome_page.pending_approval_part1_message') }}
              </div>
              <div class="part2">
                {{ $t('new_user_welcome_page.pending_approval_part2_message') }}
              </div>
              <div v-if="user?.isPendingApproval" class="already-applied-note">
                You have already applied for access. Your request is still
                pending approval.
              </div>
            </div>
            <b-button v-if="isSurrogating" @click="stopSurrogating">{{
              $t('stop_surrogating')
            }}</b-button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import SelectSites from '../Publisher/SelectSites';

export default {
  name: 'new-user-welcome-page',
  components: {
    'select-sites': SelectSites,
  },
  data() {
    return {
      onboardingStep: 1,
      primarySite: null,
      secondarySites: null,
      selectedRole: null,
      requestSubmitted: false,
    };
  },
  async created() {
    await this.ensureOnboardingData();
    this.initializeSelectionsFromCurrentUser();
  },
  methods: {
    async ensureOnboardingData() {
      const requests = [];

      if (!Array.isArray(this.sites) || this.sites.length === 0) {
        requests.push(this.$store.dispatch('getSites'));
      }

      if (!Array.isArray(this.roles) || this.roles.length === 0) {
        requests.push(this.$store.dispatch('getUserRoles'));
      }

      if (requests.length > 0) {
        await Promise.all(requests);
      }
    },
    initializeSelectionsFromCurrentUser() {
      if (this.user?.location) {
        this.primarySite = this.user.location;
      }

      if (Array.isArray(this.user?.secondaryLocations)) {
        this.secondarySites = [...this.user.secondaryLocations];
      }

      if (this.user?.role && this.requestableRoles.length > 0) {
        const matchingRole = this.requestableRoles.find(
          (role) => role.rolename === this.user.role
        );

        if (matchingRole) {
          this.selectedRole = matchingRole.rolename;
        }
      }

      if (this.hasValidPrimarySite && this.selectedRole) {
        this.onboardingStep = 2;
      }
    },
    handleSelectedSites(sites) {
      const normalizedSites = Array.isArray(sites)
        ? sites.filter((site) => Boolean(site))
        : [];

      if (normalizedSites.length > 0) {
        [this.primarySite] = normalizedSites;
        [, ...this.secondarySites] = normalizedSites;
      } else {
        this.primarySite = null;
        this.secondarySites = null;
      }
    },
    goToNextStep() {
      if (!this.hasValidPrimarySite) {
        this.$store.dispatch(
          'setWarningMessage',
          'Please select a primary location from the list before continuing.'
        );
        return;
      }

      this.onboardingStep = 2;
    },
    goToPreviousStep() {
      this.onboardingStep = 1;
    },
    async submitOnboardingRequest() {
      if (!this.hasValidPrimarySite) {
        await this.$store.dispatch(
          'setWarningMessage',
          'Please select a primary location from the list before submitting.'
        );
        return;
      }

      if (!this.selectedRole) {
        await this.$store.dispatch(
          'setWarningMessage',
          'Please select a role before submitting your request.'
        );
        return;
      }

      try {
        await this.$store.dispatch('updateUserSelf', {
          location: this.primarySite,
          secondaryLocations: this.secondarySites,
          role: this.selectedRole,
        });

        this.requestSubmitted = true;
        await this.$store.dispatch(
          'setSuccessMessage',
          'Request submitted successfully.'
        );
      } catch (error) {
        await this.$store.dispatch(
          'setErrorMessage',
          'Failed to submit request. Please try again.'
        );
      }
    },
    stopSurrogating() {
      this.$store.dispatch('clearSurrogate');
      location.reload();
    },
  },
  computed: {
    user() {
      return this.$store.state.currentUser;
    },
    sites() {
      return this.$store.state.sites || [];
    },
    roles() {
      return this.$store.state.userRoles;
    },
    requestableRoles() {
      return (this.roles || []).filter((role) => {
        const normalizedRoleName = (role.rolename || '')
          .toLowerCase()
          .trim()
          .replace(/[-_\s]/g, '');

        return (
          normalizedRoleName === 'repairengineer' ||
          normalizedRoleName === 'csm'
        );
      });
    },
    isDebugWelcomePreview() {
      return (
        process.env.NODE_ENV !== 'production' &&
        this.$route?.query?.debugPreview === '1'
      );
    },
    shouldDisplaySiteSelector() {
      if (this.requestSubmitted && !this.isDebugWelcomePreview) {
        return false;
      }

      const hasLocation = Boolean(this.user?.location);
      const hasRole = Boolean(this.user?.role);
      const hasCompletedPendingProfile = hasLocation && hasRole;

      if (this.user?.isPendingApproval) {
        return !hasCompletedPendingProfile;
      }

      if (this.user?.isRejected) {
        return true;
      }

      return this.isDebugWelcomePreview || !hasLocation;
    },
    isSurrogating() {
      return !!this.$store.state.surrogateUser;
    },
    hasValidPrimarySite() {
      if (!this.primarySite) {
        return false;
      }

      return this.sites.some((site) => site._id === this.primarySite);
    },
    selectedPrimaryLocationName() {
      if (!this.primarySite) {
        return '';
      }

      const selectedPrimarySite = this.sites.find(
        (site) => site._id === this.primarySite
      );

      return selectedPrimarySite ? selectedPrimarySite.siteName : '';
    },
    selectedSecondaryLocationNames() {
      if (
        !Array.isArray(this.secondarySites) ||
        this.secondarySites.length < 1
      ) {
        return [];
      }

      return this.secondarySites
        .map((secondarySiteId) => {
          const selectedSecondarySite = this.sites.find(
            (site) => site._id === secondarySiteId
          );
          return selectedSecondarySite ? selectedSecondarySite.siteName : null;
        })
        .filter(Boolean);
    },
    canSubmitOnboardingRequest() {
      return this.hasValidPrimarySite && !!this.selectedRole;
    },
  },
};
</script>

<style scoped lang="scss">
.welcome-page {
  min-height: calc(100vh - 40px);
  padding: 20px;
}

.onboarding-shell,
.pending-shell {
  width: 100%;
  min-height: calc(100vh - 80px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.onboarding-card,
.pending-card {
  width: 100%;
  max-width: 1100px;
  padding: 24px;
  border-radius: 8px;
  border: 1px solid;
}

.onboarding-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;

  .se-logo {
    margin-bottom: 8px;
  }
}

.welcome-message {
  text-align: center;
  font-size: 30px;
  margin: 0;
}

.onboarding-note {
  text-align: center;
  margin: 0;
  max-width: 760px;
}

.step-indicator {
  text-align: center;
  margin: 0;
}

.stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 18px;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: 0.6;
}

.step-item.active,
.step-item.done {
  opacity: 1;
  font-weight: 600;
}

.step-number {
  width: 24px;
  height: 24px;
  border: 1px solid;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}

.step-divider {
  width: 36px;
  border-top: 1px solid;
}

.onboarding-body {
  margin-bottom: 18px;
}

.step-title {
  text-align: center;
  font-size: 20px;
  margin-bottom: 6px;
}

.step-description {
  text-align: center;
  margin-bottom: 14px;
}

.selected-locations-summary {
  max-width: 500px;
  margin: 0 auto 14px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid;
}

.summary-title {
  margin: 0 0 6px;
  font-weight: 600;
}

.summary-item {
  margin: 0;
}

.summary-item + .summary-item {
  margin-top: 4px;
}

.role-picker {
  max-width: 500px;
  margin: 0 auto 20px;
}

.actions {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.se-logo {
  width: 100px;
  height: 50px;
  background-image: url('/img/se_logo_color_rgb.svg');
  background-position: center; /* Center the image */
  background-repeat: no-repeat; /* Do not repeat the image */
  margin-bottom: 50px;
}

.content {
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.dij-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: rgb(100, 30, 140);
  /*margin: 0 0 0 -4px;*/

  .abbreviation {
    font-size: 96px;
    border-right: 2px solid rgb(100, 30, 140);
    line-height: 62px;
    height: 82px;
    padding-right: 10px;
  }

  .name {
    font-size: 36px;
    font-weight: 600;
    display: flex;
    flex-direction: column;
  }

  .name-top {
    height: 48px;
  }

  .name-bottom {
    height: 48px;
    margin-bottom: 5px;
  }
}

.onboarding-dij-logo {
  margin-bottom: 6px;

  .abbreviation {
    font-size: 58px;
    line-height: 44px;
    height: 56px;
  }

  .name {
    font-size: 22px;
  }

  .name-top,
  .name-bottom {
    height: auto;
    margin-bottom: 0;
    line-height: 1.1;
  }
}

.pending-approval-message {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.already-applied-note {
  margin-top: 12px;
  font-weight: 600;
}
</style>
